import { create } from 'zustand'
import { get as apiGet, post, put, del } from '../lib/api'

export interface QrCode {
  id: string
  user_id: string
  title: string
  target_url: string
  short_code: string
  scan_count: number
  created_at: string
  updated_at: string
  scans?: QrScan[]
}

export interface QrScan {
  scanned_at: string
  country: string | null
  device: string | null
  browser: string | null
}

interface QrState {
  codes: QrCode[]
  isLoading: boolean
  error: string | null

  fetchAll: () => Promise<void>
  create: (targetUrl: string, title?: string) => Promise<QrCode | null>
  update: (id: string, data: { target_url?: string; title?: string }) => Promise<QrCode | null>
  remove: (id: string) => Promise<void>
  fetchOne: (id: string) => Promise<QrCode | null>
  clearError: () => void
}

export const useQrStore = create<QrState>((set, get) => ({
  codes:     [],
  isLoading: false,
  error:     null,

  fetchAll: async () => {
    set({ isLoading: true, error: null })
    try {
      const codes = await apiGet<QrCode[]>('/qr')
      set({ codes, isLoading: false })
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), isLoading: false })
    }
  },

  create: async (targetUrl, title = '') => {
    set({ error: null })
    try {
      const code = await post<QrCode>('/qr', { target_url: targetUrl, title })
      set({ codes: [code, ...get().codes] })
      return code
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
      return null
    }
  },

  update: async (id, data) => {
    set({ error: null })
    try {
      const code = await put<QrCode>(`/qr/${id}`, data)
      set({ codes: get().codes.map((c) => (c.id === id ? code : c)) })
      return code
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
      return null
    }
  },

  remove: async (id) => {
    set({ error: null })
    try {
      await del(`/qr/${id}`)
      set({ codes: get().codes.filter((c) => c.id !== id) })
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
    }
  },

  fetchOne: async (id) => {
    try {
      return await apiGet<QrCode>(`/qr/${id}`)
    } catch {
      return null
    }
  },

  clearError: () => set({ error: null }),
}))
